import { supabase } from '@/lib/supabase';
import { useMeditationPlayerStore } from '@/store/meditationPlayerStore';

/**
 * Музыка под дыхательную практику (13.10.2026).
 *
 * Слава: «музыку добавить, чтобы автоматом включалась на время дыхания, и чтобы был
 * видимый чек, который её отключит». Включена по умолчанию; свою музыку Слава запишет
 * отдельно — до тех пор играет запись, помеченная в базе `for_breathing`.
 *
 * Один звуковой элемент на всё приложение, вне React: практика стартует сама, без
 * касаний, а iOS разрешает звук только из обработчика нажатия. Поэтому элемент «будится»
 * в нажатии «Начать» на экране описания (prime) и на экране практики уже просто
 * набирает громкость.
 */

const KEY = 'breathing_music_enabled';
const VOLUME = 0.32; // фон: чаша и собственное дыхание должны оставаться слышны

let audio: HTMLAudioElement | null = null;
let fadeTimer: number | null = null;
type Track = { url: string; title: string };
let track: Track | null = null;
let loading: Promise<Track | null> | null = null;

export function loadMusicEnabled(): boolean {
  try { return localStorage.getItem(KEY) !== 'false'; } catch { return true; }
}

export function saveMusicEnabled(v: boolean) {
  try { localStorage.setItem(KEY, String(v)); } catch { /* не страшно */ }
}

/** Запись для дыхания: первая помеченная в базе. Нет такой — музыки нет, практика идёт без неё. */
export function loadBreathTrack(): Promise<Track | null> {
  if (track) return Promise.resolve(track);
  if (!loading) {
    loading = Promise.resolve(
      supabase.from('meditations').select('audio_url, title').eq('for_breathing', true).order('display_order').limit(1).maybeSingle(),
    ).then(({ data, error }) => {
      if (error) console.error('[breathMusic] track load failed', error);
      track = data?.audio_url ? { url: data.audio_url as string, title: data.title as string } : null;
      return track;
    });
  }
  return loading;
}

function el(): HTMLAudioElement {
  if (!audio) {
    audio = new Audio();
    audio.loop = true;
    audio.preload = 'auto';
    audio.volume = 0;
  }
  return audio;
}

function fadeTo(target: number, ms: number, then?: () => void) {
  const a = el();
  if (fadeTimer) window.clearInterval(fadeTimer);
  const from = a.volume;
  const steps = Math.max(1, Math.round(ms / 50));
  let i = 0;
  fadeTimer = window.setInterval(() => {
    i++;
    a.volume = Math.max(0, Math.min(1, from + ((target - from) * i) / steps));
    if (i >= steps) {
      if (fadeTimer) window.clearInterval(fadeTimer);
      fadeTimer = null;
      then?.();
    }
  }, 50);
}

export const breathMusic = {
  /** Вызывать ИЗ ОБРАБОТЧИКА НАЖАТИЯ: запускает звук на нулевой громкости, чтобы iOS его разрешил. */
  prime() {
    if (!track) return;
    // Медитация в общем плеере и музыка дыхания не должны звучать вместе
    try { useMeditationPlayerStore.getState().pause(); } catch { /* плеер не поднят */ }
    const a = el();
    if (a.src !== track.url) a.src = track.url;
    a.volume = 0;
    a.play().catch((e) => console.warn('[breathMusic] не удалось запустить:', e?.message || e));
  },
  /** Плавно вывести на фон. Если звук не был разбужен нажатием, попробует запустить сам. */
  start() {
    if (!track) return;
    const a = el();
    if (a.src !== track.url) a.src = track.url;
    if (a.paused) a.play().catch(() => {});
    fadeTo(VOLUME, 1800);
  },
  pause() {
    if (!audio) return;
    fadeTo(0, 400, () => audio?.pause());
  },
  /** Конец практики: уйти медленно, чтобы финал не обрывался */
  stop(ms = 2500) {
    if (!audio) return;
    fadeTo(0, ms, () => { audio?.pause(); if (audio) audio.currentTime = 0; });
  },
};
